import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { haptics } from "@/utils/haptics";
import { PrefetchLink } from "@/components/PrefetchLink";
import { getNavItemsForSegment } from "@/lib/navigation/segmentNavItems";
import { useEffectiveSegmentDetailed } from "@/hooks/useEffectiveSegment";
import { usePlatform } from "@/hooks/usePlatform";

export const BottomNavigation = () => {
  const location = useLocation();
  const { segment, isReady } = useEffectiveSegmentDetailed();
  const navItems = getNavItemsForSegment(isReady ? segment : null, { mobile: true });
  const { isIOS, isAndroidPhone } = usePlatform();

  const handleNavClick = () => {
    haptics.light();
  };

  const totalItems = navItems.length;

  const isItemActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <nav
      id="bottom-navigation"
      className={cn(
        "lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t safe-bottom",
        // iOS — translucent vibrancy / blur (UITabBar feel)
        isIOS && "bg-background/70 backdrop-blur-2xl backdrop-saturate-150 border-border/40",
        // Android — Material 3 surface with tonal elevation
        isAndroidPhone && "bg-background elev-2 border-transparent",
        // Default / desktop-mobile-test — opaque blur
        !isIOS && !isAndroidPhone && "bg-background/95 backdrop-blur-xl border-border",
      )}
    >
      <div
        className="grid h-16 sm:h-[68px]"
        style={{ gridTemplateColumns: `repeat(${totalItems}, minmax(0, 1fr))` }}
      >
        {navItems.map((item) => {
          const isActive = isItemActive(item.path);
          const Icon = item.icon;
          const label = item.shortLabel ?? item.label;

          return (
            <PrefetchLink
              key={item.path}
              to={item.path}
              onClick={handleNavClick}
              className={cn(
                "flex flex-col items-center justify-center gap-1 py-2 transition-all duration-200 touch-manipulation active:scale-95 min-w-0",
                isActive
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon
                className={cn(
                  "h-5 w-5 sm:h-[22px] sm:w-[22px] transition-transform flex-shrink-0",
                  isActive && "scale-110"
                )}
              />
              <span
                className={cn(
                  "text-[10px] sm:text-[11px] font-medium transition-all leading-none text-center truncate max-w-full px-1",
                  isActive && "font-semibold"
                )}
              >
                {label}
              </span>
            </PrefetchLink>
          );
        })}
      </div>
    </nav>
  );
};
